// client/src/components/admin/AdminDashboard.js
import { useNavigate } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useAuth from '../../hooks/useAuth'
import PageLayout from '../common/layout/PageLayout'
import ThemeToggle from './ThemeToggle'
import featureFlags from '../../config/featureFlags'
import { isOwner } from '../../utils/userRoles'
import { isProdServeSyncRuntime } from '../../utils/environmentHosts'

function AdminDashboard() {
  const navigate = useNavigate()
  const { user, isAdmin, loading } = useAuth()
  const [commit, setCommit] = useState('')
  const [copied, setCopied] = useState(false)
  const [quickOpen, setQuickOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [paletteQuery, setPaletteQuery] = useState('')
  const [reduceMotion, setReduceMotion] = useState(false)
  const touchStartY = useRef(null)
  const touchStartTime = useRef(0)
  const isProdHost = isProdServeSyncRuntime()
  const canAccessDatabaseTools = !isProdHost || isOwner(user)

  useEffect(() => {
    fetch('/commit.txt')
      .then((res) => {
        if (!res.ok) {
          throw new Error('No commit file')
        }
        return res.text()
      })
      .then((text) => {
        const trimmedText = text.trim()
        if (trimmedText && !trimmedText.startsWith('<')) {
          setCommit(trimmedText)
        }
      })
      .catch(() => {
        // File doesn't exist - that's fine, just don't show commit
      })
  }, [])

  // Note: Guarded rendering is handled after hooks to keep hooks order stable

  const goToUsers = useCallback(() => navigate('/admin/users'), [navigate])
  const goToCourts = useCallback(() => navigate('/admin/courts'), [navigate])
  const goToLeagueRegistrations = useCallback(
    () => navigate('/admin/leagues-registrations'),
    [navigate],
  )
  const goToFeedback = useCallback(
    () => navigate('/admin/feedback'),
    [navigate],
  )
  const goToInterestedUsers = useCallback(
    () => navigate('/admin/interested-users'),
    [navigate],
  )
  const goToAnalytics = useCallback(
    () => navigate('/admin/analytics'),
    [navigate],
  )
  const goToInviteCodes = useCallback(
    () => navigate('/admin/invite-codes'),
    [navigate],
  )
  const goToDbTools = useCallback(
    () => navigate('/admin/database-tools'),
    [navigate],
  )
  const goToGrantAward = useCallback(
    () => navigate('/admin/grant-award'),
    [navigate],
  )
  const goToAwardSettings = useCallback(
    () => navigate('/admin/award-settings'),
    [navigate],
  )
  const goToAwardDefinitions = useCallback(
    () => navigate('/admin/award-definitions'),
    [navigate],
  )
  const goToMonitoring = useCallback(
    () => navigate('/admin/monitoring'),
    [navigate],
  )
  const goToMatches = useCallback(() => navigate('/admin/matches'), [navigate])
  const goToAvailability = useCallback(
    () => navigate('/admin/availability'),
    [navigate],
  )
  const goToProposals = useCallback(
    () => navigate('/admin/proposals'),
    [navigate],
  )
  const goToNotifications = useCallback(
    () => navigate('/admin/notifications'),
    [navigate],
  )
  const goToFeatureFlags = useCallback(
    () => navigate('/admin/feature-flags'),
    [navigate],
  )
  const goToEmailOutbox = useCallback(
    () => navigate('/admin/email-outbox'),
    [navigate],
  )
  const goToEmailTemplateTester = useCallback(
    () => navigate('/admin/email-template-tester'),
    [navigate],
  )
  const goToSmsTester = useCallback(
    () => navigate('/admin/test-sms'),
    [navigate],
  )

  // Keyboard shortcuts: Number keys 1-9 are fixed mappings to specific admin pages.
  // IMPORTANT: The keyboard map below is hardcoded and will NOT automatically update
  // if tiles are reordered. The 'kbd' property on each tile shows which key triggers it.
  // If you reorder the tiles array, you must also update this map to match, or users
  // will see mismatched keyboard shortcuts on the tiles.
  // '/' opens command palette for fuzzy search.
  useEffect(() => {
    const handler = (e) => {
      if (
        document.activeElement &&
        (document.activeElement.tagName === 'INPUT' ||
          document.activeElement.tagName === 'TEXTAREA' ||
          document.activeElement.isContentEditable)
      ) {
        return
      }
      const key = String(e.key)
      // Fixed keyboard shortcut mapping - must match 'kbd' properties in tiles array
      const map = {
        1: goToUsers,
        2: goToLeagueRegistrations,
        4: goToCourts,
        5: goToFeedback,
        6: goToAnalytics,
        m: goToMonitoring,
        n: featureFlags.SMS_NOTIFICATIONS ? goToNotifications : null,
        7: goToInviteCodes,
        8: canAccessDatabaseTools ? goToDbTools : null,
        9: goToMatches,
        a: goToAvailability,
        p: goToProposals,
        e:
          isOwner(user) || (isAdmin && featureFlags.EMAIL_OUTBOX_ALL_ADMINS)
            ? goToEmailOutbox
            : null,
        t: goToEmailTemplateTester,
        s: goToSmsTester,
        f:
          isOwner(user) || (isAdmin && featureFlags.FEATURE_FLAGS_ALL_ADMINS)
            ? goToFeatureFlags
            : null,
        0: goToAwardSettings,
        g: goToGrantAward,
      }
      if (map[key]) {
        e.preventDefault()
        map[key]()
        return
      }
      if (key === '/') {
        e.preventDefault()
        setPaletteOpen(true)
        return
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [
    goToUsers,
    goToLeagueRegistrations,
    goToCourts,
    goToFeedback,
    goToInterestedUsers,
    goToAnalytics,
    goToInviteCodes,
    goToDbTools,
    goToMatches,
    goToAvailability,
    goToProposals,
    goToEmailOutbox,
    goToEmailTemplateTester,
    goToSmsTester,
    goToGrantAward,
    goToAwardSettings,
    goToMonitoring,
    goToNotifications,
    goToFeatureFlags,
    user,
    isAdmin,
    canAccessDatabaseTools,
  ])

  // Reduced motion preference
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReduceMotion(!!mq.matches)
    update()
    mq.addEventListener?.('change', update)
    return () => mq.removeEventListener?.('change', update)
  }, [])

  // Swipe-up from bottom edge to open Quick Actions on mobile
  useEffect(() => {
    const onStart = (e) => {
      if (window.innerWidth >= 576) return
      const touch = e.changedTouches?.[0]
      if (!touch) return
      touchStartY.current = touch.clientY
      touchStartTime.current = Date.now()
    }
    const onEnd = (e) => {
      if (window.innerWidth >= 576) return
      const touch = e.changedTouches?.[0]
      if (!touch) return
      const dy = (touchStartY.current ?? touch.clientY) - touch.clientY
      const dt = Date.now() - (touchStartTime.current || 0)
      const nearBottom = (touchStartY.current ?? 0) > window.innerHeight - 96
      if (nearBottom && dy > 50 && dt < 600) {
        setQuickOpen(true)
      }
      touchStartY.current = null
      touchStartTime.current = 0
    }
    window.addEventListener('touchstart', onStart, { passive: true })
    window.addEventListener('touchend', onEnd)
    return () => {
      window.removeEventListener('touchstart', onStart)
      window.removeEventListener('touchend', onEnd)
    }
  }, [])

  const tiles = useMemo(
    () => [
      {
        key: 'users',
        title: 'Users',
        desc: 'Manage user accounts, roles, and permissions',
        actionLabel: 'Manage Users',
        onClick: goToUsers,
        kbd: '1',
        emoji: '👤',
        track: 'admin.manage_users',
        variant: 'primary',
        group: 'league',
      },
      {
        key: 'leagues',
        title: 'Leagues',
        desc: 'Create and manage leagues, divisions, and schedules',
        actionLabel: 'Manage Leagues',
        onClick: goToLeagueRegistrations,
        kbd: '2',
        emoji: '🏆',
        track: 'admin.manage_leagues',
        variant: 'primary',
        group: 'league',
      },
      {
        key: 'courts',
        title: 'Courts',
        desc: 'Review and verify player-submitted courts',
        actionLabel: 'Review Courts',
        onClick: goToCourts,
        kbd: '4',
        emoji: '🎾',
        track: 'admin.verify_courts',
        variant: 'primary',
        group: 'league',
      },
      {
        key: 'feedback',
        title: 'Feedback',
        desc: 'View comments submitted by users',
        actionLabel: 'View Feedback',
        onClick: goToFeedback,
        kbd: '5',
        emoji: '💬',
        track: 'admin.view_feedback',
        variant: 'secondary',
        group: 'site',
      },
      {
        key: 'interestedusers',
        title: 'Interested Users',
        desc: 'View users who expressed interest without an access code',
        actionLabel: 'View Interest',
        onClick: goToInterestedUsers,
        kbd: undefined,
        emoji: '✉️',
        track: 'admin.view_interested_users',
        variant: 'primary',
        group: 'league',
      },
      {
        key: 'analytics',
        title: 'Analytics',
        desc: 'View system analytics and engagement metrics',
        actionLabel: 'View Analytics',
        onClick: goToAnalytics,
        kbd: '6',
        emoji: '📊',
        track: 'admin.view_analytics',
        variant: 'secondary',
        group: 'site',
      },
      {
        key: 'monitoring',
        title: 'Monitoring',
        desc: 'System health monitoring and incident tracking',
        actionLabel: 'View Status',
        onClick: goToMonitoring,
        kbd: 'm',
        emoji: '🔍',
        track: 'admin.view_monitoring',
        variant: 'secondary',
        group: 'site',
      },
      // Conditionally add Notifications tile if feature flag is enabled
      ...(featureFlags.SMS_NOTIFICATIONS
        ? [
            {
              key: 'notifications',
              title: 'SMS Notifications',
              desc: 'Monitor notification delivery and manage configuration',
              actionLabel: 'View Dashboard',
              onClick: goToNotifications,
              kbd: 'n',
              emoji: '📲',
              track: 'admin.view_notifications',
              variant: 'secondary',
              group: 'site',
            },
          ]
        : []),
      // Conditionally add Feature Flags tile based on owner status or feature flag
      ...(isOwner(user) || (isAdmin && featureFlags.FEATURE_FLAGS_ALL_ADMINS)
        ? [
            {
              key: 'feature-flags',
              title: 'Feature Flags',
              desc: 'Review and toggle runtime feature flags',
              actionLabel: 'Manage Flags',
              onClick: goToFeatureFlags,
              kbd: 'f',
              emoji: '🚩',
              track: 'admin.feature_flags',
              variant: 'secondary',
              group: 'site',
            },
          ]
        : []),
      {
        key: 'invites',
        title: 'Invite Codes',
        desc: 'Generate and manage signup invite codes',
        actionLabel: 'Manage Codes',
        onClick: goToInviteCodes,
        kbd: '7',
        emoji: '🎟️',
        track: 'admin.manage_invites',
        variant: 'primary',
        group: 'league',
      },
      ...(canAccessDatabaseTools
        ? [
            {
              key: 'dbtools',
              title: 'Database Tools',
              desc: isProdHost
                ? 'Seed or reset sample data (owner only on production)'
                : 'Seed or reset sample data (admin or owner)',
              actionLabel: 'Open Tools',
              onClick: goToDbTools,
              kbd: '8',
              emoji: '🗄️',
              track: 'admin.database_tools',
              variant: 'secondary',
              group: 'site',
            },
          ]
        : []),
      {
        key: 'matches',
        title: 'Upcoming Matches',
        desc: 'View all upcoming scheduled matches across leagues',
        actionLabel: 'View Matches',
        onClick: goToMatches,
        kbd: '9',
        emoji: '📅',
        track: 'admin.view_matches',
        variant: 'primary',
        group: 'league',
      },
      {
        key: 'availability',
        title: 'Player Availability',
        desc: 'View and manage all player availability entries',
        actionLabel: 'View Availability',
        onClick: goToAvailability,
        kbd: 'a',
        emoji: '🗓️',
        track: 'admin.view_availability',
        variant: 'primary',
        group: 'league',
      },
      {
        key: 'proposals',
        title: 'Match Proposals',
        desc: 'Review match proposal activity across leagues',
        actionLabel: 'View Proposals',
        onClick: goToProposals,
        kbd: 'p',
        emoji: '📨',
        track: 'admin.view_proposals',
        variant: 'primary',
        group: 'league',
      },
      // Conditionally add Email Outbox tile based on owner status or feature flag
      ...(isOwner(user) || featureFlags.EMAIL_OUTBOX_ALL_ADMINS
        ? [
            {
              key: 'email-outbox',
              title: 'Email Outbox',
              desc: 'Manage weekly activation email drafts per league',
              actionLabel: 'View Outbox',
              onClick: goToEmailOutbox,
              kbd: 'e',
              emoji: '📧',
              track: 'admin.email_outbox',
              variant: 'secondary',
              group: 'league',
            },
          ]
        : []),
      {
        key: 'email-template-tester',
        title: 'Email Template Tester',
        desc: 'Preview and send test emails with payload-driven links',
        actionLabel: 'Open Tester',
        onClick: goToEmailTemplateTester,
        kbd: 't',
        emoji: '🧪',
        track: 'admin.email_template_tester',
        variant: 'secondary',
        group: 'league',
      },
      {
        key: 'sms-tester',
        title: 'SMS Test Sender',
        desc: 'Send a direct Twilio test text to a specific phone number',
        actionLabel: 'Open Sender',
        onClick: goToSmsTester,
        kbd: 's',
        emoji: '📱',
        track: 'admin.sms_test_sender',
        variant: 'secondary',
        group: 'league',
      },
      {
        key: 'grantaward',
        title: 'Grant Award',
        desc: 'Manually grant awards to players',
        actionLabel: 'Grant Award',
        onClick: goToGrantAward,
        kbd: 'g',
        emoji: '🥇',
        track: 'admin.grant_award',
        variant: 'primary',
        group: 'league',
      },
      {
        key: 'awardsettings',
        title: 'Award Settings',
        desc: 'Configure automatic award granting',
        actionLabel: 'Settings',
        onClick: goToAwardSettings,
        kbd: '0',
        emoji: '⚙️',
        track: 'admin.award_settings',
        variant: 'primary',
        group: 'league',
      },
      {
        key: 'awarddefinitions',
        title: 'Award Definitions',
        desc: 'Create and edit award types with custom icons',
        actionLabel: 'Manage Awards',
        onClick: goToAwardDefinitions,
        kbd: '-',
        emoji: '🏅',
        track: 'admin.award_definitions',
        variant: 'primary',
        group: 'league',
      },
    ],
    [
      user,
      goToUsers,
      goToLeagueRegistrations,
      goToCourts,
      goToFeedback,
      goToInterestedUsers,
      goToAnalytics,
      goToMonitoring,
      goToInviteCodes,
      goToDbTools,
      goToMatches,
      goToAvailability,
      goToProposals,
      goToEmailOutbox,
      goToEmailTemplateTester,
      goToSmsTester,
      goToGrantAward,
      goToAwardSettings,
      goToAwardDefinitions,
      goToNotifications,
      goToFeatureFlags,
      isAdmin,
      isProdHost,
      canAccessDatabaseTools,
    ],
  )

  const handleCopyCommit = useCallback(() => {
    if (!commit) return
    const doAfter = () => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1200)
    }
    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(commit)
        .then(doAfter)
        .catch(() => {})
    } else {
      try {
        const ok = window.prompt('Copy commit SHA:', commit)
        if (ok !== null) doAfter()
      } catch {
        // Ignore prompt errors
      }
    }
  }, [commit])

  const heroGradient = {
    background:
      'radial-gradient(1200px 400px at 0% 0%, rgba(13,110,253,.25), transparent 60%), radial-gradient(1200px 400px at 100% 0%, rgba(25,135,84,.25), transparent 60%)',
    borderRadius: '1rem',
  }

  const cardHoverStyle = useMemo(
    () => ({
      transition: reduceMotion
        ? 'box-shadow 150ms ease, background-color 150ms ease'
        : 'transform 180ms ease, box-shadow 200ms ease, background-color 200ms ease',
    }),
    [reduceMotion],
  )

  const vibrateTiny = () => {
    try {
      if (navigator.vibrate) navigator.vibrate(10)
    } catch {
      // Ignore vibration errors
    }
  }

  const badgeKbd = (txt) => (
    <span
      className="ms-2 small text-nowrap"
      style={{
        border: '1px solid rgba(0,0,0,.175)',
        borderRadius: '6px',
        padding: '1px 6px',
        background: 'rgba(255,255,255,.6)',
      }}
      aria-hidden="true"
    >
      {txt}
    </span>
  )

  const Tile = ({ item }) => (
    <div className="col-6 col-md-4 col-lg-3">
      {/* Ultra-compact mobile-first tile */}
      <div className="d-sm-none">
        <button
          type="button"
          className="btn btn-light w-100 h-100 p-3 border rounded-3 shadow-sm"
          onClick={() => {
            vibrateTiny()
            item.onClick()
          }}
          aria-label={item.title}
          style={{ minHeight: '88px' }}
        >
          <div style={{ fontSize: '1.75rem' }} aria-hidden>
            {item.emoji}
          </div>
          <div className="mt-1 text-truncate" style={{ fontSize: '0.95rem' }}>
            {item.title}
          </div>
        </button>
      </div>

      {/* Original card experience for tablet/desktop */}
      <div
        className="d-none d-sm-block card h-100 shadow-sm border-0"
        style={cardHoverStyle}
        role="button"
        tabIndex={0}
        onClick={() => {
          vibrateTiny()
          item.onClick()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            vibrateTiny()
            item.onClick()
          }
        }}
        onMouseEnter={(e) => {
          if (reduceMotion) return
          e.currentTarget.style.transform = 'translateY(-2px)'
          e.currentTarget.style.boxShadow = '0 0.8rem 1.2rem rgba(0,0,0,.08)'
        }}
        onMouseLeave={(e) => {
          if (reduceMotion) return
          e.currentTarget.style.transform = 'none'
          e.currentTarget.style.boxShadow = '0 .5rem 1rem rgba(0,0,0,.05)'
        }}
      >
        <div className="card-body d-flex flex-column item-start p-3">
          <div className="d-flex align-items-center mb-3">
            <div
              className="me-2 text-center"
              aria-hidden="true"
              style={{ fontSize: '1.75rem', width: '2rem', lineHeight: 1 }}
            >
              {item.emoji}
            </div>
            <h5 className="card-title mb-0 fs-6 fs-sm-5">{item.title}</h5>
          </div>
          <p className="card-text flex-grow-1 text-muted d-none d-lg-block mb-3 px-1">
            {item.desc}
          </p>
          <button
            className={`btn btn-${item.variant} w-100 mt-auto py-2`}
            onClick={() => {
              vibrateTiny()
              item.onClick()
            }}
            data-track={item.track}
            aria-label={`${item.actionLabel} (Press ${item.kbd})`}
          >
            {item.actionLabel}
            {badgeKbd(item.kbd)}
          </button>
        </div>
      </div>
    </div>
  )

  // Early exits after all hooks are declared
  if (loading) {
    return (
      <PageLayout title="Admin Dashboard">
        <div className="container py-4">
          <p>Loading...</p>
        </div>
      </PageLayout>
    )
  }

  if (!isAdmin) {
    return (
      <PageLayout title="Admin Dashboard">
        <div className="container py-4">
          <p>Access denied. You are not an admin.</p>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout title="Admin Dashboard">
      <div className="container py-2 py-sm-4">
        <div
          className="p-2 p-sm-4 p-md-5 mb-3 mb-sm-5 shadow-sm position-relative overflow-hidden"
          style={heroGradient}
        >
          <div
            className="position-absolute top-0 start-0 w-100 h-100"
            aria-hidden="true"
            style={{
              background:
                'linear-gradient(135deg, rgba(255,255,255,.35), rgba(255,255,255,.05))',
              backdropFilter: 'blur(6px)',
            }}
          />
          <div className="position-relative">
            <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
              <h2 className="mb-0 text-center fs-6 fs-sm-3">
                Welcome to the Serve Sync admin panel
              </h2>
              <span
                className="badge bg-primary-subtle text-primary border border-primary-subtle"
                title={`Signed in as ${user?.email || 'admin'}`}
              >
                {(() => {
                  const t = user?.user_type || 'admin'
                  return t.charAt(0).toUpperCase() + t.slice(1)
                })()}
              </span>
              <button
                type="button"
                className="btn btn-light btn-sm ms-2 border d-none d-sm-inline-flex"
                aria-label="Open command palette"
                onClick={() => setPaletteOpen(true)}
                title="Search (/)"
              >
                🔎
              </button>
              {/* Mobile search moved to floating action button (FAB) */}
              {commit && commit.length > 0 && (
                <div
                  className="d-inline-flex align-items-center gap-2 small text-muted px-2 py-1 rounded-pill bg-white bg-opacity-75 border"
                  data-testid="commit-sha"
                  title="Click to copy commit SHA"
                  role="button"
                  tabIndex={0}
                  onClick={handleCopyCommit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleCopyCommit()
                  }}
                  style={{ backdropFilter: 'blur(6px)' }}
                >
                  <span>Commit:</span>
                  <code
                    className="text-body text-truncate"
                    style={{ maxWidth: 120, display: 'inline-block' }}
                  >
                    {commit}
                  </code>
                  <span className="text-secondary" aria-live="polite">
                    {copied ? 'Copied' : 'Copy'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {(() => {
          const leagueTiles = tiles.filter((t) => t.group === 'league')
          const siteTiles = tiles.filter((t) => t.group === 'site')

          const Section = ({ title, subtitle, items, testId }) => (
            <section className="mb-4" data-testid={testId}>
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                <div>
                  <h3 className="h6 h5-sm mb-0">{title}</h3>
                  {subtitle ? (
                    <div className="text-muted small">{subtitle}</div>
                  ) : null}
                </div>
              </div>
              <div className="row justify-content-center g-3 g-sm-3 g-lg-4">
                {items.map((t) => (
                  <Tile key={t.key} item={t} />
                ))}
              </div>
            </section>
          )

          return (
            <>
              <Section
                title="League Management"
                subtitle="Players, leagues, locations, and awards"
                items={leagueTiles}
                testId="admin-dashboard-section-league"
              />
              <hr className="my-3 my-sm-4" />
              <Section
                title="Site & Operations"
                subtitle="Admin utilities, monitoring, and product insights"
                items={siteTiles}
                testId="admin-dashboard-section-site"
              />
              <hr className="my-3 my-sm-4" />
              <section
                className="mb-4"
                data-testid="admin-dashboard-section-theme"
              >
                <div className="mb-3">
                  <h3 className="h6 h5-sm mb-0">Appearance</h3>
                  <div className="text-muted small">
                    Customize the application theme
                  </div>
                </div>
                <ThemeToggle />
              </section>
            </>
          )
        })()}
        <div className="mt-3 text-center text-muted small d-none d-sm-block">
          Tip: Use keyboard shortcuts shown on buttons to quickly navigate.
        </div>
        {/* Spacer for mobile bottom nav to avoid overlap */}
        <div className="d-sm-none" style={{ height: 72 }} />
      </div>

      {/* Floating Action Button for mobile search */}
      <button
        type="button"
        className="btn btn-primary rounded-circle position-fixed d-sm-none shadow"
        aria-label="Search admin actions"
        title="Search"
        onClick={() => setPaletteOpen(true)}
        style={{
          width: 56,
          height: 56,
          right: 16,
          bottom: `calc(16px + env(safe-area-inset-bottom, 0px))`,
          zIndex: 1040,
        }}
      >
        🔎
      </button>

      {/* Quick actions speed-dial sheet */}
      {quickOpen && (
        <div
          className="d-sm-none position-fixed top-0 start-0 w-100 h-100"
          style={{ zIndex: 1039 }}
        >
          <div
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{ background: 'rgba(0,0,0,.25)' }}
            role="button"
            aria-label="Close overlay"
            onClick={() => setQuickOpen(false)}
          />
          <div
            className="position-absolute start-50 translate-middle-x"
            style={{
              bottom: 'calc(120px + env(safe-area-inset-bottom, 0px))',
              width: 'min(92vw, 520px)',
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Quick actions"
          >
            <div className="card shadow-lg border-0">
              <div className="card-body p-3">
                <div className="row g-3 text-center">
                  {tiles.map((t) => (
                    <div key={`qa-${t.key}`} className="col-4">
                      <button
                        type="button"
                        className="btn btn-light w-100 h-100 p-3 border rounded-3"
                        onClick={() => {
                          setQuickOpen(false)
                          vibrateTiny()
                          t.onClick()
                        }}
                        aria-label={t.title}
                      >
                        <div style={{ fontSize: '1.5rem' }} aria-hidden>
                          {t.emoji}
                        </div>
                        <div className="small mt-1 text-truncate">
                          {t.title}
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Command Palette */}
      {paletteOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ zIndex: 1050 }}
        >
          <div
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{ background: 'rgba(0,0,0,.35)' }}
            role="button"
            aria-label="Close command palette"
            onClick={() => setPaletteOpen(false)}
          />
          <div
            className="position-absolute top-50 start-50 translate-middle"
            style={{ width: 'min(95vw, 680px)', margin: '0 1rem' }}
          >
            <div className="card shadow-lg border-0">
              <div className="card-body p-2 p-sm-3">
                <input
                  autoFocus
                  type="text"
                  className="form-control form-control-lg"
                  style={{ fontSize: '16px' }} // Prevents zoom on iOS
                  placeholder="Search admin actions..."
                  value={paletteQuery}
                  onChange={(e) => setPaletteQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setPaletteOpen(false)
                  }}
                  aria-label="Search admin actions"
                />
                <div
                  className="list-group list-group-flush mt-2"
                  role="listbox"
                >
                  {tiles
                    .filter(
                      (t) =>
                        !paletteQuery ||
                        t.title
                          .toLowerCase()
                          .includes(paletteQuery.toLowerCase()) ||
                        t.desc
                          .toLowerCase()
                          .includes(paletteQuery.toLowerCase()),
                    )
                    .map((t) => (
                      <button
                        key={`cp-${t.key}`}
                        type="button"
                        className="list-group-item list-group-item-action d-flex align-items-center gap-3 py-3"
                        style={{ minHeight: '60px' }} // Better touch targets
                        onClick={() => {
                          vibrateTiny()
                          setPaletteOpen(false)
                          t.onClick()
                        }}
                        role="option"
                        aria-selected="false"
                        aria-label={t.title}
                      >
                        <span style={{ fontSize: '1.25rem' }} aria-hidden>
                          {t.emoji}
                        </span>
                        <div className="text-start">
                          <div className="fw-semibold">{t.title}</div>
                          <div className="text-muted small d-none d-sm-block">
                            {t.desc}
                          </div>
                        </div>
                        <div className="ms-auto small text-muted">{t.kbd}</div>
                      </button>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  )
}

export default AdminDashboard
